import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { useModal } from '../../hooks/useModal';
import { referenceService } from '../../services/api';

const OrganizationManagement = ({ user }) => {
  const canManage = ['Administrator', 'Manager'].includes(user?.role);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);

  // Hide navbar when any modal is open
  useModal(showModal || showEditModal || showViewModal, () => {
    setShowModal(false);
    setShowViewModal(false);
    setShowEditModal(false);
  });

  const [formData, setFormData] = useState({
    name: '',
    abbreviation: '',
    ntn: '',
    city: '',
    country: '',
    address: '',
    contactPerson: '',
    designation: '',
    email: '',
    phone: '',
    location: ''
  });
  const [errors, setErrors] = useState({});

  const validateField = (name, value) => {
    let error = null;
    if (name === 'name') {
      const isDuplicate = organizations.some(org =>
        org.name.toLowerCase() === value.toLowerCase() &&
        (selectedOrg ? org.id !== selectedOrg.id : true)
      );
      if (isDuplicate) error = 'Organization Name already exists!';
    }
    if (name === 'ntn' && value) {
      const isDuplicate = organizations.some(org =>
        org.ntn === value &&
        (selectedOrg ? org.id !== selectedOrg.id : true)
      );
      if (isDuplicate) error = 'NTN No already exists!';
    }
    setErrors(prev => ({ ...prev, [name]: error }));
  };

  const handleChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      const response = await referenceService.getOrgs();
      const sortedOrgs = response.data.sort((a, b) => a.name.localeCompare(b.name));
      setOrganizations(sortedOrgs);
      setLoading(false);
    } catch (error) {
      console.error('Error loading organizations:', error);
      Swal.fire('Error', 'Failed to load organizations', 'error');
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedOrg) {
        await referenceService.updateOrg(selectedOrg.id, formData);
        Swal.fire('Success', 'Organization updated successfully!', 'success');
        setShowEditModal(false);
        setSelectedOrg(null);
        loadOrganizations();
      } else {
        await referenceService.createOrg(formData);
        Swal.fire('Success', 'Organization added successfully!', 'success');
        setShowModal(false);
        loadOrganizations();
      }
      // Reset form
      setFormData({
        name: '',
        abbreviation: '',
        city: '',
        country: '',
        address: '',
        contactPerson: '',
        designation: '',
        email: '',
        phone: '',
        location: ''
      });

    } catch (error) {
      console.error("Full Error Object:", error);
      console.error("Error Response:", error.response);
      console.error("Error Data:", error.response?.data);

      let errorMessage = 'Failed to save organization';

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (typeof error.response?.data === 'string') {
        errorMessage = error.response.data; // Use raw string if returned
      } else if (error.message) {
        errorMessage = error.message;
      }

      Swal.fire('Error', errorMessage, 'error');
      // Do NOT close the modal here to allow user to correction
    }
  };

  const handleEdit = (org) => {
    setSelectedOrg(org);
    setFormData({
      name: org.name,
      abbreviation: org.abbreviation || '',
      ntn: org.ntn || '',
      city: org.city || '',
      country: org.country || '',
      address: org.address || '',
      contactPerson: org.contactPerson || '',
      designation: org.designation || '',
      email: org.email || '',
      phone: org.phone || '',
      location: org.location || ''
    });
    setErrors({});
    setShowEditModal(true);
  };

  const handleView = (org) => {
    setSelectedOrg(org);
    setShowViewModal(true);
  };

  const handleDelete = (orgId) => {
    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await referenceService.deleteOrg(orgId);
          Swal.fire('Deleted!', 'Organization has been deleted.', 'success');
          loadOrganizations();
        } catch (error) {
          console.error("Delete error:", error);
          Swal.fire({
            icon: 'error',
            title: 'Cannot Delete Organization',
            text: error.response?.data?.error || 'Failed to delete organization.',
          });
        }
      }
    });
  };

  const filteredOrganizations = organizations.filter(org =>
    !searchTerm ||
    org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (org.abbreviation && org.abbreviation.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (org.ntn && org.ntn.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (org.city && org.city.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (org.country && org.country.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (org.contactPerson && org.contactPerson.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-start mb-4">
        <div>
          <h2 className="mb-1" style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: '700',
            fontSize: '2rem'
          }}>
            Organization Management
          </h2>
          <p className="text-muted mb-0">Manage organizations and contacts</p>
        </div>

        {canManage && (
          <button className="btn btn-primary" onClick={() => {
            setErrors({});
            setShowModal(true);
          }}>
            <i className="bi bi-plus-circle"></i> Add Organization
          </button>
        )}
      </div>

      <div className="card">
        <div className="card-header">
          <h5 className="mb-0">All Organizations</h5>
        </div>
        <div className="card-body">
          <div className="mb-3">
            <input
              type="text"
              className="form-control"
              placeholder="Search organizations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>NTN</th>
                  <th>Abbreviation</th>
                  <th>City</th>
                  <th>Country</th>
                  <th>Contact Person</th>
                  <th>Designation</th>
                  <th style={{ minWidth: '200px' }}>Location</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="8" className="text-center">Loading...</td></tr>
                ) : filteredOrganizations.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center text-muted">
                      No organizations found
                    </td>
                  </tr>
                ) : (
                  filteredOrganizations.map((org) => (
                    <tr key={org.id}>
                      <td><strong>{org.name}</strong></td>
                      <td>{org.ntn || '-'}</td>
                      <td><span className="badge bg-secondary">{org.abbreviation}</span></td>
                      <td>{org.city}</td>
                      <td>{org.country}</td>
                      <td>{org.contactPerson}</td>
                      <td>{org.designation || '-'}</td>
                      <td>{org.location || org.address || '-'}</td>
                      <td>{org.email}</td>
                      <td>{org.phone}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-outline-info me-2"
                          onClick={() => handleView(org)}
                          title="View Details"
                        >
                          <i className="bi bi-eye"></i>
                        </button>
                        {canManage && (
                          <>
                            <button
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => handleEdit(org)}
                              title="Edit"
                            >
                              <i className="bi bi-pencil"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(org.id)}
                              title="Delete"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Add Modal */}
      {
        showModal && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Add New Organization</h5>
                  <button type="button" className="btn-close" onClick={() => setShowModal(false)}>
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="row g-3">
                      <div className="col-12 mb-3">
                        <label className="form-label">Organization Name *</label>
                        <input
                          type="text"
                          className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                          value={formData.name}
                          onChange={(e) => handleChange('name', e.target.value)}
                          required
                        />
                        {errors.name && <div className="invalid-feedback d-block">{errors.name}</div>}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Abbreviation</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.abbreviation}
                          onChange={(e) => setFormData({ ...formData, abbreviation: e.target.value })}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">NTN</label>
                        <input
                          type="text"
                          className={`form-control ${errors.ntn ? 'is-invalid' : ''}`}
                          value={formData.ntn}
                          onChange={(e) => handleChange('ntn', e.target.value)}
                        />
                        {errors.ntn && <div className="invalid-feedback d-block">{errors.ntn}</div>}
                      </div>

                    </div>
                    <div className="row g-3">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">City</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.city}
                          onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Country</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.country}
                          onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                        />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Address</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      ></textarea>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Contact Person</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.contactPerson}
                          onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Designation</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.designation}
                          onChange={(e) => setFormData({ ...formData, designation: e.target.value })}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Email</label>
                        <input
                          type="email"
                          className="form-control"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Phone</label>
                        <input
                          type="tel"
                          className="form-control"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Add Organization
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )
      }

      {/* Edit Modal */}
      {
        showEditModal && selectedOrg && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit Organization</h5>
                  <button type="button" className="btn-close" onClick={() => {
                    setShowEditModal(false);
                    setSelectedOrg(null);
                  }}></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="row g-3">
                      <div className="col-12 mb-3">
                        <label className="form-label">Organization Name *</label>
                        <input
                          type="text"
                          className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                          value={formData.name}
                          onChange={(e) => handleChange('name', e.target.value)}
                          required
                        />
                        {errors.name && <div className="invalid-feedback d-block">{errors.name}</div>}
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Abbreviation</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData.abbreviation}
                          onChange={(e) => setFormData({ ...formData, abbreviation: e.target.value })}
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">NTN</label>
                        <input
                          type="text"
                          className={`form-control ${errors.ntn ? 'is-invalid' : ''}`}
                          value={formData.ntn}
                          onChange={(e) => handleChange('ntn', e.target.value)}
                        />
                        {errors.ntn && <div className="invalid-feedback d-block">{errors.ntn}</div>}
                      </div>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">City</label>
                        <input type="text" className="form-control" value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Country</label>
                        <input type="text" className="form-control" value={formData.country} onChange={(e) => setFormData({ ...formData, country: e.target.value })} />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Address</label>
                      <textarea className="form-control" rows="2" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })}></textarea>
                    </div>
                    <div className="row g-3">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Contact Person</label>
                        <input type="text" className="form-control" value={formData.contactPerson} onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })} />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Designation</label>
                        <input type="text" className="form-control" value={formData.designation} onChange={(e) => setFormData({ ...formData, designation: e.target.value })} />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Email</label>
                        <input type="email" className="form-control" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Phone</label>
                        <input type="tel" className="form-control" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => {
                      setShowEditModal(false);
                      setSelectedOrg(null);
                    }}>
                      Cancel
                    </button>
                    <button type="submit" className="btn btn-primary">Update Organization</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )
      }

      {/* View Modal */}
      {
        showViewModal && selectedOrg && (
          <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header bg-info text-white">
                  <h5 className="modal-title">Organization Details</h5>
                  <button type="button" className="btn-close btn-close-white" onClick={() => {
                    setShowViewModal(false);
                    setSelectedOrg(null);
                  }}></button>
                </div>
                <div className="modal-body">
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="fw-bold">Name:</label>
                      <p>{selectedOrg.name}</p>
                    </div>
                    <div className="col-md-6">
                      <label className="fw-bold">Abbreviation:</label>
                      <p>{selectedOrg.abbreviation || '-'}</p>
                    </div>
                    <div className="col-md-6">
                      <label className="fw-bold">NTN:</label>
                      <p>{selectedOrg.ntn || '-'}</p>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="fw-bold">City:</label>
                      <p>{selectedOrg.city || '-'}</p>
                    </div>
                    <div className="col-md-6">
                      <label className="fw-bold">Country:</label>
                      <p>{selectedOrg.country || '-'}</p>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="fw-bold">Contact Person:</label>
                      <p>{selectedOrg.contactPerson || '-'}</p>
                    </div>
                    <div className="col-md-6">
                      <label className="fw-bold">Designation:</label>
                      <p>{selectedOrg.designation || '-'}</p>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label className="fw-bold">Email:</label>
                      <p>{selectedOrg.email || '-'}</p>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="fw-bold">Location:</label>
                    <p>{selectedOrg.location || '-'}</p>
                  </div>
                  <div className="mb-3">
                    <label className="fw-bold">Address:</label>
                    <p>{selectedOrg.address || '-'}</p>
                  </div>
                  <div className="mb-3">
                    <label className="fw-bold">Phone:</label>
                    <p>{selectedOrg.phone || '-'}</p>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => {
                    setShowViewModal(false);
                    setSelectedOrg(null);
                  }}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div>
  );
};

export default OrganizationManagement;
